import {
	BackgroundAttributes,
	BackgroundControl,
	SpacingAttributes,
	SpacingControl,
	TypographyAttributes,
	TypographyControl,
} from '@redfox-thank-you/control-groups';
import { BaseControl, ButtonGroup, RangeControl } from '@redfox-thank-you/controls';
import { InspectorControls } from '@wordpress/block-editor';
import { PanelBody, ToggleControl } from '@wordpress/components';
import { applyFilters } from '@wordpress/hooks';
import { __ } from '@wordpress/i18n';
import React from 'react';

import { headingLevelOptions } from '../constants';
import { TableColorsComponent } from './components/table-colors';
import type { SettingsProps, TableColorsAttributes } from './types';

/**
 * Order Details block settings component (Inspector Controls).
 */
export const Settings: React.FC< SettingsProps > = ( {
	updateAttributes,
	getAttribute,
} ) => {
	// Default values for reset functionality
	const defaultValues = {
		showTitle: true,
		titleLevel: 2,
		showProductImages: true,
		productImageSize: 50,
		showSubtotal: true,
		showPaymentMethod: true,
	};

	// Helper functions for specific attribute types
	const updateTypography = (
		section: 'title' | 'blockText',
		typographyAttributes: Partial< TypographyAttributes >,
	) => {
		const stylePath =
			section === 'blockText' ? 'style.block.text' : `style.${ section }`;
		updateAttributes( stylePath, typographyAttributes );
	};

	const updateBackground = (
		backgroundAttributes: Partial< BackgroundAttributes >,
	) => {
		updateAttributes( 'style.block.background', backgroundAttributes );
	};

	const updateSpacing = (
		spacingAttributes: Partial< SpacingAttributes >,
	) => {
		updateAttributes( 'style.block.spacing', spacingAttributes );
	};

	const updateTableColors = (
		tableColorAttributes: Partial< TableColorsAttributes >,
	) => {
		updateAttributes( 'style.table', tableColorAttributes );
	};

	// Extract style properties with defaults
	const blockTextStyle = getAttribute< TypographyAttributes >(
		'style.block.text',
		{},
	);
	const blockBackgroundStyle = getAttribute< BackgroundAttributes >(
		'style.block.background',
		{},
	);
	const blockSpacingStyle = getAttribute< SpacingAttributes >(
		'style.block.spacing',
		{},
	);
	const titleStyle = getAttribute< TypographyAttributes >(
		'style.title',
		{},
	);
	const tableColors = getAttribute< TableColorsAttributes >(
		'style.table',
		{},
	);

	// Extract settings properties with defaults
	const titleLevel = getAttribute(
		'settings.title.level',
		defaultValues.titleLevel,
	);
	const showTitle = getAttribute(
		'settings.title.show',
		defaultValues.showTitle,
	);
	const showProductImages = getAttribute(
		'settings.display.productImages',
		defaultValues.showProductImages,
	);
	const showSubtotal = getAttribute(
		'settings.display.subtotal',
		defaultValues.showSubtotal,
	);
	const showPaymentMethod = getAttribute(
		'settings.display.paymentMethod',
		defaultValues.showPaymentMethod,
	);
	const productImageSize = getAttribute(
		'settings.layout.productImageSize',
		defaultValues.productImageSize,
	);

	return (
		<>
			<InspectorControls>
				{ /* Title Settings */ }
				<PanelBody
					title={ __( 'Title Settings', 'redfox-thank-you' ) }
					initialOpen={ true }
				>
					<BaseControl
						id="show-title"
						label={ __( 'Show Title', 'redfox-thank-you' ) }
						help={ __(
							'Display or hide the block title.',
							'redfox-thank-you',
						) }
						inline
						helpAsTooltip
						showResetButton={ true }
						hasValue={ showTitle !== defaultValues.showTitle }
						onReset={ () =>
							updateAttributes(
								'settings.title.show',
								defaultValues.showTitle,
							)
						}
					>
						<ToggleControl
							label=""
							checked={ showTitle }
							onChange={ ( value ) =>
								updateAttributes( 'settings.title.show', value )
							}
							__nextHasNoMarginBottom
						/>
					</BaseControl>

					{ showTitle && (
						<BaseControl
							id="heading-level"
							label={ __( 'Heading Level', 'redfox-thank-you' ) }
							help={ __(
								'Choose the semantic heading level for SEO and accessibility.',
								'redfox-thank-you',
							) }
							showResetButton={ true }
							hasValue={ titleLevel !== defaultValues.titleLevel }
							onReset={ () =>
								updateAttributes(
									'settings.title.level',
									defaultValues.titleLevel,
								)
							}
						>
							<ButtonGroup
								value={ titleLevel.toString() }
								options={ headingLevelOptions }
								onChange={ ( value ) =>
									updateAttributes(
										'settings.title.level',
										parseInt( value || '2' ),
									)
								}
								allowDeselect={ false }
							/>
						</BaseControl>
					) }
				</PanelBody>

				{ /* Display Settings */ }
				<PanelBody
					title={ __( 'Display Options', 'redfox-thank-you' ) }
					initialOpen={ false }
				>
					<BaseControl
						id="show-product-images"
						label={ __(
							'Show Product Images',
							'redfox-thank-you',
						) }
						help={ __(
							'Display product images in the order details.',
							'redfox-thank-you',
						) }
						inline
						showResetButton={ true }
						hasValue={
							showProductImages !==
							defaultValues.showProductImages
						}
						onReset={ () =>
							updateAttributes(
								'settings.display.productImages',
								defaultValues.showProductImages,
							)
						}
					>
						<ToggleControl
							label=""
							checked={ showProductImages }
							onChange={ ( value ) =>
								updateAttributes(
									'settings.display.productImages',
									value,
								)
							}
							__nextHasNoMarginBottom
						/>
					</BaseControl>

					{ showProductImages && (
						<BaseControl
							id="product-image-size"
							label={ __(
								'Product Image Size',
								'redfox-thank-you',
							) }
							help={ __(
								'Set the size of product images in pixels.',
								'redfox-thank-you',
							) }
							showResetButton={ true }
							hasValue={
								productImageSize !==
								defaultValues.productImageSize
							}
							onReset={ () =>
								updateAttributes(
									'settings.layout.productImageSize',
									defaultValues.productImageSize,
								)
							}
						>
							<RangeControl
								value={ productImageSize }
								onChange={ ( value ) =>
									updateAttributes(
										'settings.layout.productImageSize',
										value ?? 50,
									)
								}
								min={ 30 }
								max={ 150 }
								step={ 5 }
							/>
						</BaseControl>
					) }

					<BaseControl
						id="show-subtotal"
						label={ __( 'Show Subtotal', 'redfox-thank-you' ) }
						help={ __(
							'Display subtotal in the order details.',
							'redfox-thank-you',
						) }
						inline
						showResetButton={ true }
						hasValue={ showSubtotal !== defaultValues.showSubtotal }
						onReset={ () =>
							updateAttributes(
								'settings.display.subtotal',
								defaultValues.showSubtotal,
							)
						}
					>
						<ToggleControl
							label=""
							checked={ showSubtotal }
							onChange={ ( value ) =>
								updateAttributes(
									'settings.display.subtotal',
									value,
								)
							}
							__nextHasNoMarginBottom
						/>
					</BaseControl>

					<BaseControl
						id="show-payment-method"
						label={ __(
							'Show Payment Method',
							'redfox-thank-you',
						) }
						help={ __(
							'Display payment method in the order details.',
							'redfox-thank-you',
						) }
						inline
						showResetButton={ true }
						hasValue={
							showPaymentMethod !==
							defaultValues.showPaymentMethod
						}
						onReset={ () =>
							updateAttributes(
								'settings.display.paymentMethod',
								defaultValues.showPaymentMethod,
							)
						}
					>
						<ToggleControl
							label=""
							checked={ showPaymentMethod }
							onChange={ ( value ) =>
								updateAttributes(
									'settings.display.paymentMethod',
									value,
								)
							}
							__nextHasNoMarginBottom
						/>
					</BaseControl>
				</PanelBody>
			</InspectorControls>

			<InspectorControls group="styles">
				{ /* Typography Controls */ }
				<TypographyControl
					title={ __( 'Text', 'redfox-thank-you' ) }
					initialOpen={ true }
					attributes={ blockTextStyle }
					setAttributes={ ( attrs ) =>
						updateTypography( 'blockText', attrs )
					}
				/>

				{ showTitle && (
					<TypographyControl
						title={ __( 'Title Text', 'redfox-thank-you' ) }
						initialOpen={ false }
						attributes={ titleStyle }
						setAttributes={ ( attrs ) =>
							updateTypography( 'title', attrs )
						}
						enabledControls={ {
							wordSpacing: true,
						} }
					/>
				) }

				{ /**
				 * Filter to add additional typography controls to the Order Details block.
				 * Pro plugins can use this to add table header, product name, price, etc. typography controls.
				 *
				 * @param {JSX.Element | null} controls - Default null, Pro plugin can return JSX elements.
				 * @param {Object} props - Props containing getAttribute and updateAttributes functions.
				 * @param {Function} props.getAttribute - Function to get attribute values.
				 * @param {Function} props.updateAttributes - Function to update attributes.
				 * @param {Object} props.displaySettings - Display settings (showSubtotal, showPaymentMethod).
				 */ }
				{ applyFilters(
					'redfox_thank_you.order_details.typography_controls',
					null,
					{
						getAttribute,
						updateAttributes,
						displaySettings: {
							showSubtotal,
							showPaymentMethod,
						},
					},
				) as React.ReactNode }

				{ /**
				 * Filter to add additional product image controls to the Order Details block.
				 * Pro plugins can use this to add sizing, border, and box shadow controls.
				 *
				 * @param {JSX.Element | null} controls - Default null, Pro plugin can return JSX elements.
				 * @param {Object} props - Props containing getAttribute and updateAttributes functions.
				 * @param {Function} props.getAttribute - Function to get attribute values.
				 * @param {Function} props.updateAttributes - Function to update attributes.
				 * @param {boolean} props.showProductImages - Whether product images are enabled.
				 */ }
				{ showProductImages &&
					( applyFilters(
						'redfox_thank_you.order_details.product_image_controls',
						null,
						{
							getAttribute,
							updateAttributes,
						},
					) as React.ReactNode ) }

				{ /* Background Controls */ }
				<BackgroundControl
					title={ __( 'Background', 'redfox-thank-you' ) }
					initialOpen={ false }
					responsive={ true }
					attributes={ blockBackgroundStyle }
					setAttributes={ updateBackground }
					enabledControls={ {
						backgroundColor: true,
						backgroundImage: true,
						backgroundGradient: true,
						backgroundPattern: false,
						backgroundOverlay: true,
						advancedOptions: true,
						responsive: true,
					} }
				/>

				{ /* Spacing Controls */ }
				<SpacingControl
					title={ __( 'Spacing', 'redfox-thank-you' ) }
					initialOpen={ false }
					attributes={ blockSpacingStyle }
					setAttributes={ updateSpacing }
					enabledControls={ {
						padding: true,
						margin: true,
						responsive: true,
						presets: true,
						boxModel: true,
						autoValues: true,
					} }
					units={ [ 'px', 'em', 'rem', '%' ] }
					min={ 0 }
					max={ 200 }
					allowReset={ true }
				/>

				{ /* Table Colors */ }
				<TableColorsComponent
					attributes={ tableColors }
					setAttributes={ updateTableColors }
					title={ __( 'Table Colors', 'redfox-thank-you' ) }
					initialOpen={ false }
					showResponsive={ true }
				/>
			</InspectorControls>
		</>
	);
};
